import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { EditorContent, mergeAttributes, Node, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { AtSign, Bold, Code, Italic, Link2, List, ListOrdered, Paperclip } from 'lucide-react';
import { safeHref, type Member } from '@kaba/shared';
import { LinkBar, useLinkDialog } from './LinkTools';
import { Avatar, cx } from '../ui/ui';

/**
 * Erwähnung als eigenes Element mit Personen-ID (nicht als Text): Umbenennen oder gleiche Namen
 * stören nicht. Im HTML: <span data-mention="<id>" data-label="Name">@Name</span>
 */
const Mention = Node.create({
  name: 'mention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,
  addAttributes() {
    return {
      id: { default: null, parseHTML: (el) => el.getAttribute('data-mention'), renderHTML: (a) => ({ 'data-mention': a.id as string }) },
      label: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-label') ?? (el.textContent ?? '').replace(/^@/, ''),
        renderHTML: (a) => ({ 'data-label': a.label as string }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-mention]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes({ class: 'kaba-mention' }, HTMLAttributes), `@${node.attrs.label as string}`];
  },
  renderText({ node }) {
    return `@${node.attrs.label as string}`;
  },
});

export type CommentEditorHandle = { clear: () => void; focus: () => void };

type Query = { q: string; from: number; to: number };

/**
 * Kommentar-Editor: Enter = neuer Absatz, Strg/Cmd+Enter = senden. Kompakte Leiste (Fett, Kursiv,
 * Listen, Code, Link, Erwähnen, Datei). Eingefügte oder hineingezogene Dateien gehen an `onFiles`
 * und nie zusätzlich an die Karte (`data-own-paste`).
 */
export function CommentEditor({
  initial,
  members,
  meId,
  onChange,
  onSubmit,
  onFiles,
  onEscape,
  autoFocus,
  label,
  placeholder,
  footer,
  handle,
}: {
  initial: string;
  members: Member[];
  meId?: string;
  onChange: (html: string) => void;
  onSubmit: () => void;
  onFiles?: (files: File[]) => void;
  onEscape?: () => void;
  autoFocus?: boolean;
  label: string;
  placeholder?: string;
  /** Rechts in der unteren Leiste (z. B. Senden) */
  footer?: ReactNode;
  handle?: Ref<CommentEditorHandle>;
}) {
  const [query, setQuery] = useState<Query | null>(null);
  const [active, setActive] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(
    () => (query ? members.filter((m) => m.id !== meId && m.name.toLowerCase().includes(query.q.toLowerCase())).slice(0, 6) : []),
    [query, members, meId],
  );
  const cb = useRef({ onChange, onSubmit, onFiles, onEscape, suggestions, active, query });
  cb.current = { onChange, onSubmit, onFiles, onEscape, suggestions, active, query };

  const detect = (e: Editor) => {
    const { $from, empty } = e.state.selection;
    if (!empty) return setQuery(null);
    const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 40), $from.parentOffset, undefined, '￼');
    const m = before.match(/(?:^|\s)@([\p{L}\p{N}._-]*)$/u);
    if (!m) return setQuery(null);
    setQuery((prev) => (prev && prev.q === m[1] && prev.to === $from.pos ? prev : { q: m[1]!, from: $from.pos - m[1]!.length - 1, to: $from.pos }));
    setActive(0);
  };

  const pick = (e: Editor, m: Member) => {
    const q = cb.current.query;
    if (!q) return;
    e.chain()
      .focus()
      .insertContentAt({ from: q.from, to: q.to }, [
        { type: 'mention', attrs: { id: m.id, label: m.name } },
        { type: 'text', text: ' ' },
      ])
      .run();
    setQuery(null);
  };

  const filesOf = (list: FileList | null | undefined) => (list ? Array.from(list) : []);

  const editor = useEditor({
    immediatelyRender: true,
    editable: true,
    content: initial,
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        underline: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https', HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' } },
      }),
      Mention,
      Placeholder.configure({ placeholder: placeholder ?? 'Kommentar schreiben … (@ für Erwähnung)' }),
    ],
    editorProps: {
      attributes: {
        class: 'kaba-prose kaba-comment min-h-16 max-h-72 overflow-y-auto px-3 py-2 outline-none text-base sm:text-sm leading-relaxed',
        'aria-label': label,
        'aria-multiline': 'true',
        role: 'textbox',
      },
      handleKeyDown: (_view, e) => {
        const { suggestions: s, active: a } = cb.current;
        if (s.length) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((x) => (x + (e.key === 'ArrowDown' ? 1 : -1) + s.length) % s.length);
            return true;
          }
          if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'Tab') {
            e.preventDefault();
            if (edRef.current) pick(edRef.current, s[a]!);
            return true;
          }
          if (e.key === 'Escape') {
            setQuery(null);
            return true;
          }
        }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          cb.current.onSubmit();
          return true;
        }
        return false;
      },
      handlePaste: (_view, e) => {
        const files = filesOf(e.clipboardData?.files);
        if (!files.length || !cb.current.onFiles) return false;
        e.preventDefault();
        cb.current.onFiles(files);
        return true;
      },
      handleDrop: (_view, e) => {
        const files = filesOf((e as DragEvent).dataTransfer?.files);
        setDragOver(false);
        if (!files.length || !cb.current.onFiles) return false;
        e.preventDefault();
        cb.current.onFiles(files);
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      cb.current.onChange(e.isEmpty ? '' : e.getHTML());
      detect(e);
    },
    onSelectionUpdate: ({ editor: e }) => detect(e),
    onBlur: () => window.setTimeout(() => setQuery(null), 150),
  });

  const edRef = useRef<Editor | null>(null);
  edRef.current = editor;
  const zone = useRef<HTMLDivElement>(null);

  useImperativeHandle(handle, () => ({
    clear: () => editor?.commands.clearContent(true),
    focus: () => editor?.commands.focus('end'),
  }));

  // Escape im Editor: erst die Personenliste schließen, sonst `onEscape` (Bearbeiten abbrechen)
  useEffect(() => {
    if (!editor) return;
    const on = () => {
      if (cb.current.suggestions.length) setQuery(null);
      else cb.current.onEscape?.();
    };
    const dom = zone.current;
    dom?.addEventListener('kaba-escape', on);
    return () => dom?.removeEventListener('kaba-escape', on);
  }, [editor]);

  useEffect(() => {
    if (!autoFocus || !editor) return;
    const t = requestAnimationFrame(() => editor.commands.focus('end'));
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            code: e.isActive('code'),
            bullet: e.isActive('bulletList'),
            ordered: e.isActive('orderedList'),
            link: e.isActive('link'),
          }
        : null,
  });

  const link = useLinkDialog(editor);
  if (!editor) return null;
  const btn = (activeNow: boolean | undefined, name: string, icon: ReactNode, run: () => void) => (
    <button
      type="button"
      aria-label={name}
      title={name}
      aria-pressed={activeNow === undefined ? undefined : !!activeNow}
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
      className={cx(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 hover:text-[var(--ink)] sm:size-8 dark:hover:bg-white/10',
        activeNow && 'bg-black/10 text-[var(--ink)] dark:bg-white/15',
      )}
    >
      {icon}
    </button>
  );

  return (
    <div
      ref={zone}
      data-own-paste
      // Escape gehört dem Editor nur, solange die Personenliste offen ist oder gerade bearbeitet wird – sonst schließt es die Karte
      data-keep-escape={suggestions.length > 0 || onEscape ? '' : undefined}
      className={cx('relative rounded-xl border bg-[var(--panel)] focus-within:border-accent', dragOver ? 'border-accent ring-2 ring-accent/30' : 'border-[var(--line)]')}
      onDragOver={(e) => {
        if (!onFiles || !e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        // Abseits des Textfelds (Leiste, Rand) fallen gelassen
        setDragOver(false);
        const files = filesOf(e.dataTransfer.files);
        if (!files.length || !onFiles || e.defaultPrevented) return;
        e.preventDefault();
        onFiles(files);
      }}
    >
      <EditorContent editor={editor} />
      <LinkBar editor={editor} onEdit={link.start} />
      {link.dialog}
      <div className="flex items-center gap-1 border-t border-[var(--line)] px-1 py-1">
        {/* Am Handy scrollt die Leiste waagerecht, Senden bleibt rechts stehen */}
        <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto" role="toolbar" aria-label="Werkzeuge">
          {onFiles && (
            <>
              {btn(undefined, 'Datei oder Bild anhängen', <Paperclip size={16} />, () => fileInput.current?.click())}
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const files = filesOf(e.target.files);
                  if (files.length) onFiles(files);
                  e.target.value = '';
                }}
              />
            </>
          )}
          {btn(undefined, 'Person erwähnen', <AtSign size={16} />, () => {
            const { $from } = editor.state.selection;
            const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 1), $from.parentOffset);
            editor.chain().focus().insertContent(before && !/\s/.test(before) ? ' @' : '@').run();
          })}
          <span className="mx-0.5 h-5 w-px shrink-0 bg-[var(--line)]" aria-hidden />
          {btn(state?.bold, 'Fett', <Bold size={16} />, () => editor.chain().focus().toggleBold().run())}
          {btn(state?.italic, 'Kursiv', <Italic size={16} />, () => editor.chain().focus().toggleItalic().run())}
          {btn(state?.bullet, 'Aufzählung', <List size={16} />, () => editor.chain().focus().toggleBulletList().run())}
          {btn(state?.ordered, 'Nummerierte Liste', <ListOrdered size={16} />, () => editor.chain().focus().toggleOrderedList().run())}
          {btn(state?.code, 'Code', <Code size={16} />, () => editor.chain().focus().toggleCode().run())}
          {btn(state?.link, state?.link ? 'Link bearbeiten' : 'Link einfügen', <Link2 size={16} />, link.start)}
        </div>
        <div className="flex shrink-0 items-center gap-2">{footer}</div>
      </div>
      {suggestions.length > 0 && (
        <ul role="listbox" aria-label="Person erwähnen" className="absolute top-full left-0 z-20 mt-1 w-64 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)] shadow-xl">
          {suggestions.map((m, i) => (
            <li key={m.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(editor, m)}
                className={cx('flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm', i === active && 'bg-accent-soft')}
              >
                <Avatar user={m} size={24} /> {m.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Anzeige ---------- */

const BLOCK: Record<string, string> = { p: 'p', ul: 'ul', ol: 'ol', li: 'li', blockquote: 'blockquote', pre: 'pre' };
const INLINE: Record<string, string> = { strong: 'strong', em: 'em', s: 's', code: 'code', br: 'br' };

/**
 * Zeigt Kommentar-HTML an, ohne es einzuschleusen: Der Text wird mit DOMParser (führt nichts aus)
 * gelesen und Element für Element nach einer Positivliste in React-Elemente übersetzt.
 * Erwähnungen zeigen den aktuellen Namen der Person.
 */
export function CommentHtml({ html, members }: { html: string; members: Member[] }) {
  const tree = useMemo(() => new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body, [html]);
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m.name])), [members]);
  const render = (node: ChildNode, key: number): ReactNode => {
    if (node.nodeType === 3) return node.textContent;
    if (node.nodeType !== 1) return null;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const kids = () => Array.from(el.childNodes).map((c, i) => render(c, i));
    if (tag === 'span') {
      const id = el.getAttribute('data-mention');
      if (!id) return <Fragment key={key}>{kids()}</Fragment>;
      const name = byId.get(id) ?? el.getAttribute('data-label') ?? (el.textContent ?? '').replace(/^@/, '');
      return (
        <span key={key} className="kaba-mention">
          @{name}
        </span>
      );
    }
    if (tag === 'a') {
      const href = safeHref(el.getAttribute('href') ?? '');
      if (!href) return <Fragment key={key}>{kids()}</Fragment>;
      return (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">
          {kids()}
        </a>
      );
    }
    if (tag === 'ol') {
      const start = Number(el.getAttribute('start'));
      return (
        <ol key={key} start={Number.isInteger(start) && start > 1 ? start : undefined}>
          {kids()}
        </ol>
      );
    }
    const T = (BLOCK[tag] ?? INLINE[tag]) as keyof React.JSX.IntrinsicElements | undefined;
    if (!T) return <Fragment key={key}>{kids()}</Fragment>;
    if (T === 'br') return <br key={key} />;
    return <T key={key}>{kids()}</T>;
  };
  return <div className="kaba-prose kaba-comment text-sm leading-relaxed break-words">{Array.from(tree.childNodes).map((c, i) => render(c, i))}</div>;
}

/**
 * Getippte Erwähnungen ohne Auswahl aus der Liste („@Anna Maria“) beim Senden in echte
 * Erwähnungen mit Personen-ID umwandeln. Längere Namen zuerst; nicht in Code, Links oder
 * bestehenden Erwähnungen.
 */
export function linkTypedMentions(html: string, members: Member[], meId?: string): string {
  const people = members.filter((m) => m.id !== meId && m.name.trim()).sort((a, b) => b.name.length - a.name.length);
  if (!people.length || !html.includes('@')) return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if ((n.parentElement as Element | null)?.closest('span[data-mention], code, pre, a')) continue;
    if (n.textContent?.includes('@')) texts.push(n as Text);
  }
  if (!texts.length) return html;
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(^|[^\\p{L}\\p{N}_])@(${people.map((p) => esc(p.name)).join('|')})(?![\\p{L}\\p{N}_])`, 'gu');
  let changed = false;
  for (const t of texts) {
    const src = t.textContent ?? '';
    const parts: (string | Member)[] = [];
    let last = 0;
    for (const m of src.matchAll(re)) {
      const person = people.find((p) => p.name === m[2]);
      if (!person) continue;
      const at = m.index! + m[1]!.length;
      parts.push(src.slice(last, at), person);
      last = at + 1 + m[2]!.length;
    }
    if (!parts.length) continue;
    parts.push(src.slice(last));
    const frag = doc.createDocumentFragment();
    for (const p of parts) {
      if (typeof p === 'string') {
        if (p) frag.appendChild(doc.createTextNode(p));
      } else {
        const span = doc.createElement('span');
        span.setAttribute('data-mention', p.id);
        span.setAttribute('data-label', p.name);
        span.textContent = `@${p.name}`;
        frag.appendChild(span);
      }
    }
    t.replaceWith(frag);
    changed = true;
  }
  return changed ? doc.body.innerHTML : html;
}
